import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Sparkles } from "lucide-react";
import { BUSINESS } from "../../data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/domestic-cleaning", label: "Domestic Cleaning" },
  { to: "/mobile-car-valeting", label: "Mobile Car Valeting" },
  { to: "/prices", label: "Prices" },
  { to: "/gallery", label: "Gallery" },
  { to: "/book", label: "Book Online" },
  { to: "/contact", label: "Contact Us" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_20px_rgba(31,58,95,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-navy text-white group-hover:bg-brandgreen transition-colors duration-300">
              <Sparkles className="w-5 h-5" strokeWidth={2.2} />
            </span>
            <span className="leading-none">
              <span className="block font-heading font-extrabold text-navy text-[15px] sm:text-base tracking-tight">
                Mark Clean
              </span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-brandgreen font-semibold">
                Services Ltd
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-200 ${
                    isActive ? "text-brandgreen" : "text-navy/80 hover:text-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Call button + mobile toggle */}
          <div className="flex items-center gap-2">
            <a
              href={BUSINESS.phoneHref}
              data-testid="call-now-header"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brandgreen px-4 py-2.5 text-white text-[13px] font-semibold hover:bg-navy transition-colors duration-300 hover:scale-[1.03] active:scale-95"
            >
              <Phone className="w-4 h-4" strokeWidth={2.4} />
              Call Now: {BUSINESS.phone}
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="lg:hidden grid place-items-center w-10 h-10 rounded-lg text-navy hover:bg-mist transition-colors"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
            data-testid="mobile-nav"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `py-3 text-base font-medium border-b border-black/5 last:border-0 ${
                      isActive ? "text-brandgreen" : "text-navy"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={BUSINESS.phoneHref}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brandgreen px-4 py-3 text-white font-semibold"
              >
                <Phone className="w-4 h-4" /> Call Now: {BUSINESS.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
